import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { StripeCardElementOptions, CreatePaymentMethodCardData } from '@stripe/stripe-js';
import { roundedCorners } from '../../QmoTheme';
import { CircularProgress, TextField, Button, Box, Typography, FormGroup, FormControlLabel, Checkbox, Grid, Select, MenuItem, InputLabel, FormControl } from '@material-ui/core';
import { PaymentCard } from '../../api/models/OrderData';
import { ArrowForward, ArrowBack } from '@material-ui/icons';


const CARD_ELEMENT_OPTIONS: StripeCardElementOptions = {
    style: {
        base: {
            color: "#32325d",
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSmoothing: "antialiased",
            fontSize: "16px",
            "::placeholder": {
                color: "#aab7c4",
            },
        },
        invalid: {
            color: "#fa755a",
            iconColor: "#fa755a",
        },
    },
    hidePostalCode: true
};


interface IProps {
    clientSecret: string | null,
    paymentCards: PaymentCard[] | null
    finalPrice: number,
    currency: string,
    onSuccess: () => void
}

export default function StripePaymentForm({ clientSecret, finalPrice, paymentCards, currency, onSuccess }: IProps) {
    const stripe = useStripe();
    const elements = useElements();
    const [errorMessage, setErrorMessage] = useState("")
    const [saveCardInfo, setSaveCardInfo] = useState(false)
    const [cardholderName, setCardholderName] = useState("")
    const [busy, setBusy] = useState(false)
    const [useNewCard, setUseNewCard] = useState((!paymentCards || paymentCards.length === 0))
    const [selectedSavedCard, setSelectedSavedCard] = useState<string | null>((paymentCards && paymentCards.length > 0) ? paymentCards[0].id : null)


    useEffect(() => {
        setCardholderName("")
    }, [useNewCard])


    const handleSubmit = async (event: any) => {
        event.preventDefault();

        if (!stripe || !elements) {
            console.log('Not loaded yet!')
            // Stripe.js has not yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }
        setBusy(true)



        let method: string | Omit<CreatePaymentMethodCardData, 'type'> | null = null
        if (useNewCard) {
            method = {
                card: elements.getElement(CardElement)!,
                billing_details: {
                    name: cardholderName,
                }
            }
        }
        else
            if (selectedSavedCard !== null)
                method = selectedSavedCard
            else
                throw new Error("Select proper payment card.")


        const result = await stripe.confirmCardPayment(clientSecret!, {
            payment_method: method!,
            setup_future_usage: (saveCardInfo ? "off_session" : null)
        });

        setBusy(false)
        if (result.error) {
            // Show error to your customer (e.g., insufficient funds)
            console.log(result.error.message);
            setErrorMessage(result.error.message!)
        } else {
            // The payment has been processed!
            if (result.paymentIntent!.status === 'succeeded') {
                onSuccess()
            }
        }
    };

    return (
        <Box>
            <form onSubmit={handleSubmit}>
                <Box p={1} textAlign="right">
                    {(paymentCards && paymentCards.length > 0) &&
                        <Button variant="text" onClick={() => setUseNewCard(!useNewCard)}>{!useNewCard ? <></> : <ArrowBack />} Use {useNewCard ? "saved" : "new"} card {!useNewCard ? <ArrowForward /> : <></>}</Button>
                    }
                </Box>

                {(paymentCards && paymentCards.length > 0 && !useNewCard) &&
                    <>
                        <Box p={2} paddingLeft={0} paddingRight={0}>
                            <FormControl variant="outlined" style={{ width: '100%' }}>
                                <InputLabel style={{ backgroundColor: 'white' }}>Your cards</InputLabel>
                                <Select
                                    onChange={(e: any) => setSelectedSavedCard(e.target.value)}
                                    value={selectedSavedCard}
                                >
                                    {paymentCards?.map((pc, i) => {
                                        return (<MenuItem value={pc.id} key={i}>{pc.country} {pc.brand.toUpperCase()} **** **** **** {pc.dynamicLast4}</MenuItem>)
                                    })}
                                </Select>
                            </FormControl>
                        </Box>
                    </>
                }

                {useNewCard &&
                    <>
                        <Box paddingLeft={0} paddingRight={0}>
                            <TextField
                                required
                                style={{ width: '100%' }}
                                variant="outlined"
                                placeholder="JOHN DOE"
                                label="Cardholder name"
                                margin='normal'
                                value={cardholderName}
                                onChange={(e) => {
                                    if (e.target.value === '' || /^[A-z]+(\s[A-z]*)*$/.test(e.target.value))
                                        setCardholderName(e.target.value.toUpperCase())
                                }}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />

                        </Box>

                        <Box p={1} paddingLeft={0} paddingRight={0}>
                            <div style={Object.assign({}, { padding: "16px", minWidth: "280px" }, roundedCorners, { borderRadius: '50px' })}>
                                <CardElement onChange={() => setErrorMessage("")} options={CARD_ELEMENT_OPTIONS} />
                            </div>
                        </Box>
                        <Box paddingLeft={1} paddingRight={1}>
                            <Typography color="error">{errorMessage}</Typography>
                        </Box>
                        <Box style={{ float: 'right' }}>
                            <FormGroup row>
                                <FormControlLabel
                                    control={
                                        <Checkbox color="primary" checked={saveCardInfo} onChange={(e) => {
                                            setSaveCardInfo(e.target.checked)
                                        }}></Checkbox>
                                    }
                                    label="Save card?"
                                /></FormGroup>
                        </Box>

                    </>
                }
                <Box p={1} paddingLeft={0} paddingRight={0}>
                    <Button style={{ minWidth: '120px', width: '100%' }} type="submit" variant="contained" color="secondary"
                        onClick={() => setErrorMessage("")}
                        disabled={!stripe || busy}>
                        {busy ?
                            <CircularProgress size={20} style={{ color: 'white' }} />
                            :
                            <>Pay {currency}{finalPrice}</>
                        }

                    </Button>
                </Box>
            </form>

            <div style={{ display: (busy ? 'block' : 'none'), top: 0, left: 0, backgroundColor: "#fafafa", opacity: 0.45, position: "absolute", zIndex: 10000, width: '100%', height: '100%' }}></div>
        </Box>
    );
}